/**
 * ProductPhoto — the product-detail photograph, whole, and openable full-screen.
 *
 * WHY IT IS ITS OWN COMPONENT (SCRUM-230). The photo used to be six lines
 * inline in `ProductDetailPage`: a 4:3 box with `object-cover` and no click
 * target. QA measured the consequence — an 800×450 source rendered into a
 * 382×287 frame, both sides of every landscape photo discarded, and tapping it
 * did nothing because no dialog existed to open. Giving the photo its own
 * component is what lets the crop and the lightbox be tested at all; folded
 * back into a 700-line page they could only be checked by eye, which is how
 * they shipped.
 *
 * `object-contain`, not `object-cover`. Cover fills the frame and throws away
 * the overflow — that IS the crop. Contain fits the whole image inside and
 * letterboxes it against the frame's own `--g-sunken` surface, so the grid
 * keeps its 4:3 rhythm while the photograph stays intact. A catalogue whose
 * sources are a mix of 16:9 phone shots and square studio images cannot use a
 * single cropping frame without losing part of something.
 *
 * The trigger is a real `<button>`. A click handler on a bare `<img>` would
 * satisfy the ticket for a mouse and fail every keyboard user — and this is a
 * product used one-handed on a phone, where the tap target has to be the whole
 * photo rather than an icon in a corner.
 *
 * The modal follows the house pattern (`chat/components/DeleteConfirmDialog`):
 * the project ships no shadcn `Dialog` primitive, so this is a self-contained
 * `role="dialog"` + `aria-modal` overlay built from design tokens, with Escape
 * to dismiss, an overlay click to dismiss, focus moved in on open and restored
 * to the trigger on close.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

/** Shown when a photo 404s, and when a product has no photo at all. */
export const PRODUCT_PHOTO_PLACEHOLDER = "/placeholder/200x200.svg";

export interface ProductPhotoProps {
  /** Absolute or CDN-relative image URL; falsy renders the placeholder. */
  src: string | undefined;
  /** Product name — used for the trigger's accessible name, not as alt text. */
  name: string;
}

export function ProductPhoto({ src, name }: ProductPhotoProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  const resolved = src && src.length > 0 ? src : PRODUCT_PHOTO_PLACEHOLDER;

  const close = useCallback(() => {
    setOpen(false);
    // Focus returns to where it came from, or the reader is dropped at the top
    // of the document with no idea where they were.
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    closeRef.current?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const onImgError = (e: React.SyntheticEvent<HTMLImageElement>): void => {
    if (e.currentTarget.src.endsWith(PRODUCT_PHOTO_PLACEHOLDER)) return;
    e.currentTarget.src = PRODUCT_PHOTO_PLACEHOLDER;
  };

  return (
    <>
      <div className="overflow-hidden rounded-[var(--g-radius)] border border-[color:var(--g-hairline)] bg-[color:var(--g-sunken)] lg:sticky lg:top-4">
        <button
          type="button"
          ref={triggerRef}
          data-testid="product-detail-image-trigger"
          onClick={() => setOpen(true)}
          aria-label={t("productDetail.viewPhoto", { name })}
          className="block w-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <img
            src={resolved}
            alt=""
            loading="lazy"
            onError={onImgError}
            data-testid="product-detail-image"
            className="aspect-[4/3] w-full object-contain"
          />
        </button>
      </div>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          data-testid="product-lightbox-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-testid="product-lightbox"
            className="flex max-h-full w-full max-w-4xl flex-col gap-3"
          >
            <div className="flex items-center justify-between gap-3">
              <h2
                id={titleId}
                className="truncate text-sm font-medium text-white"
              >
                {name}
              </h2>
              <button
                type="button"
                ref={closeRef}
                onClick={close}
                data-testid="product-lightbox-close"
                className="min-h-[44px] rounded-[var(--g-radius)] px-3 py-1 text-sm font-medium text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {t("productDetail.closePhoto")}
              </button>
            </div>
            <img
              src={resolved}
              alt={name}
              onError={onImgError}
              data-testid="product-lightbox-image"
              className="max-h-[80vh] w-full object-contain"
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
